import { Button, Colors, TextFieldWrapper, ToastVariant } from "@factorearth/component-library";
import { FormGroup, UserForResolver } from "db/definitions";
import React, { useEffect, useMemo, useState } from "react";
import { FiChevronDown, FiChevronUp, FiEdit } from "react-icons/fi";
import { useLocation } from "react-router";
import _updateFormGroupName from "utils/_updateFormGroupName";
import { recordSentry } from "utils/recordSentry";
import styles from "./FormGroupListItem.module.scss";
import SavedLocally from "assets/icons/SavedLocallyIcon";
import { getFormGroupById } from "db/queries/formGroupQueries";
import { GoDotFill } from "react-icons/go";

interface ToastContent {
	title: string;
	message: string;
	type: ToastVariant;
}
interface Props {
	addFormGroupsInState?: (formGroups: FormGroup[]) => void;
	color: Colors;
	formGroup: FormGroup;
	handleOpen: (formGroup: FormGroup) => void;
	handleSelect: (formGroup: FormGroup) => void;
	online: boolean;
	selected: string[];
	user: UserForResolver;
	forceUpdate?: boolean;
	setOpenToast?: React.Dispatch<React.SetStateAction<boolean>>;
	setToastContent?: React.Dispatch<React.SetStateAction<ToastContent>>;
}

function FormGroupListItem(props: Props) {
	const { addFormGroupsInState, color, formGroup, handleOpen, handleSelect, online, selected, user } = props;

	const [expanded, setExpanded] = useState(false);
	const [editingText, setEditingText] = useState(false);
	const [formGroupName, setFormGroupName] = useState(formGroup.name);
	const [loading, setLoading] = useState(false);
	const location = useLocation();
	const [offlineFormGroup, setOfflineFormGroup] = useState<FormGroup | undefined>(undefined);
	const [syncedColorState, setSyncedColorState] = useState<string | undefined>(undefined);
	const [syncedDateString, setSyncedDateString] = useState<string | undefined>("undefined");
	const [updatedDateString, setUpdatedDateString] = useState<string>(formGroup.updatedAt ? new Date(formGroup.updatedAt).toLocaleString() : "");
	const [lastUpdatedBy, setLastUpdatedBy] = useState<string>(formGroup?.lastUpdatedBy || "");

	useEffect(() => {
		setFormGroupName(formGroup.name);
	}, [formGroup.name]);

	useEffect(() => {
		async function fetchOfflineFormGroup() {
			const updatedDate = new Date(formGroup.updatedAt);
			if (!formGroup.offlineAndOnline) return;
			const fetchedFormGroup = await getFormGroupById(formGroup.id);
			setOfflineFormGroup(fetchedFormGroup);
			const offlineUpdatedDate = fetchedFormGroup?.updatedAt ? new Date(fetchedFormGroup.updatedAt) : undefined;
			const offlineSyncedDate = fetchedFormGroup?.syncedAt ? new Date(fetchedFormGroup.syncedAt) : undefined;

			let syncedDateString, syncedDate;
			if (!formGroup.syncedAt) {
				if (fetchedFormGroup && offlineSyncedDate) {
					syncedDateString = offlineSyncedDate.toLocaleString();
				} else {
					syncedDateString = "";
				}
			} else {
				syncedDate = new Date(formGroup.syncedAt);
				syncedDateString = syncedDate.toLocaleString();
			}
			const updatedDateTime = updatedDate.getTime();
			const offlineUpdatedDateTime = offlineUpdatedDate ? offlineUpdatedDate.getTime() : undefined;
			const syncedDateTime = syncedDate ? syncedDate.getTime() : undefined;
			const offlineSyncedDateTime = offlineSyncedDate ? offlineSyncedDate.getTime() : undefined;
			// When Online Check against Online Updated Date
			if (online && syncedDateTime && syncedDateTime > updatedDateTime) {
				setSyncedColorState(color.buttonBackground.success);
			} else if (online && syncedDateTime && syncedDateTime < updatedDateTime) {
				setSyncedColorState("orange");
			}
			// When Offline Compare Offline Synced Date with Offline Updated Date
			if (!online && offlineSyncedDateTime && offlineUpdatedDateTime && offlineSyncedDateTime < offlineUpdatedDateTime) {
				setSyncedColorState("orange");
			} else if (!online && !offlineSyncedDateTime) {
				// If there is no offline synced date, we assume it is not synced yet.
				setSyncedColorState("orange");
			}
			// Compare Offline Updated Date with Online Updated Date against the Synced Date
			if (online && offlineUpdatedDateTime && syncedDateTime && syncedDateTime > updatedDateTime && syncedDateTime > offlineUpdatedDateTime) {
				setSyncedColorState(color.buttonBackground.success);
			} else if (online && offlineUpdatedDateTime && syncedDateTime && (syncedDateTime < updatedDateTime || syncedDateTime < offlineUpdatedDateTime)) {
				setSyncedColorState("orange");
			}

			// Accounts for existing offline form groups that have not been synced yet, and the initial offline sync of online form groups
			if (online && !syncedDateTime && offlineSyncedDateTime && offlineUpdatedDateTime && offlineUpdatedDateTime === updatedDateTime) {
				setSyncedColorState(color.buttonBackground.success);
			} else if (online && !syncedDateTime && offlineSyncedDateTime && offlineUpdatedDateTime && offlineUpdatedDateTime !== updatedDateTime) {
				setSyncedColorState("orange");
			} else if (online && !syncedDateTime && !offlineSyncedDateTime && offlineUpdatedDateTime && offlineUpdatedDateTime === updatedDateTime) {
				setSyncedColorState(color.buttonBackground.success);
			} else if (online && !syncedDateTime && !offlineSyncedDateTime && offlineUpdatedDateTime && offlineUpdatedDateTime !== updatedDateTime) {
				setSyncedColorState("orange");
			}

			if (online && offlineUpdatedDate && offlineUpdatedDateTime && updatedDateTime && offlineUpdatedDateTime > updatedDateTime) {
				setUpdatedDateString(offlineUpdatedDate?.toLocaleString());
				setLastUpdatedBy(fetchedFormGroup?.lastUpdatedBy || "");
			}
			setSyncedDateString(syncedDateString);
		}
		fetchOfflineFormGroup();
	}, [formGroup, online, props.forceUpdate]);

	// useEffect(() => {
	// 	async function updateFormGroupName() {
	// 		const trimmedName = formGroupName.trim();

	// 		if (!editingText && trimmedName === "" && formGroup.name !== trimmedName) {
	// 			setFormGroupName(formGroup.name);
	// 			return;
	// 		}
	// 		if (!editingText && trimmedName && formGroup.name !== trimmedName && addFormGroupsInState) {
	// 			try {
	// 				setLoading(true);
	// 				const formGroupNameOnly = {
	// 					id: formGroup.id,
	// 					group: formGroup.group,
	// 					name: trimmedName,
	// 				};
	// 				const newInitial = {
	// 					...formGroup,
	// 					name: trimmedName,
	// 				};
	// 				addFormGroupsInState([newInitial]);
	// 				await _updateFormGroupName(formGroupNameOnly, online, user.username);
	// 				setLoading(false);
	// 			} catch (error) {
	// 				recordSentry({ error, msg: `Error updating form group name on formGroup ${formGroup.id}`, showModal: true });
	// 			}
	// 		}
	// 	}
	// 	updateFormGroupName();
	// }, [editingText, formGroupName, online, formGroup]);

	// Handle name update only on user action (onBlur or Enter)
	const handleNameUpdate = async () => {
		const trimmedName = formGroupName.trim();
		if (trimmedName === "" && formGroup.name !== trimmedName) {
			setFormGroupName(formGroup.name);
			return;
		}
		if (trimmedName && formGroup.name !== trimmedName && addFormGroupsInState) {
			try {
				setLoading(true);
				const formGroupNameOnly = {
					id: formGroup.id,
					group: formGroup.group,
					name: trimmedName,
				};
				const newInitial = {
					...formGroup,
					name: trimmedName,
				};
				addFormGroupsInState([newInitial]);
				await _updateFormGroupName(formGroupNameOnly, online, user.username);
				setLoading(false);
			} catch (error) {
				recordSentry({ error, msg: `Error updating form group name on formGroup ${formGroup.id}`, showModal: true });
			}
		}
	};

	const instances = useMemo(() => {
		const itemMap: Record<string, React.JSX.Element> = {};
		const items = [];
		if (formGroup.formInstances) {
			for (let i = 0; i < formGroup.formInstances.items.length; i++) {
				const formInstance = formGroup.formInstances.items[i];
				const instanceHtml = (
					<li className={styles.formtemplate_bullet} key={`listitem-${formInstance.id}`}>
						{formInstance.formTemplate?.versionName
							? `${formInstance.formTemplate?.template} - ${formInstance.formTemplate?.versionName}`
							: formInstance.formTemplate?.template}
					</li>
				);
				itemMap[formInstance.id] = instanceHtml;
			}
			for (let i = 0; i < formGroup.formOrder.length; i++) {
				const id = formGroup.formOrder[i];
				items.push(itemMap[id]);
			}
		}
		const html = (
			<ul className={styles.formtemplate_list} key={formGroup.id}>
				{items}
			</ul>
		);
		return html;
	}, [formGroup.formInstances?.items, formGroup.formOrder, formGroup.id, props.forceUpdate]);

	const menu = useMemo(() => {
		const html = (
			<>
				<div style={{ color: color.text.primary }} className={styles.menu_info_wrapper}>
					<div>
						<div className={styles.menu_detail}>
							<div className={styles.menu_label}>Last Updated:</div>
							<div style={{ color: color.text.primary }}> {updatedDateString}</div>
						</div>
						<div className={styles.menu_detail}>
							<div className={styles.menu_label}>Last Updated By:</div>
							<div style={{ color: color.text.primary }}> {lastUpdatedBy}</div>
						</div>
						<div className={styles.menu_detail} style={{ color: syncedColorState ? syncedColorState : color.text.primary }}>
							<div className={styles.menu_label}>Last Synced:</div>
							<div style={{ color: color.text.primary }}> {syncedDateString}</div>
						</div>
					</div>
					<div className={styles.menu_instances}>
						<div className={styles.menu_detail}>
							<div className={styles.menu_label}>Type:</div>
							<div style={{ color: color.text.primary }}>{formGroup.type}</div>
						</div>
						<div className={styles.menu_detail}>
							<div className={styles.menu_label}>Form Instances:</div>
							<div style={{ color: color.text.primary }}>{instances}</div>
						</div>
					</div>
					{/* {user?.isDeveloper && !location.pathname.includes("sync-menu") && import.meta.env.VITE_APP_ENV === "development" ? <GenerateTestData formGroup={formGroup} /> : null} */}
				</div>
			</>
		);
		return html;
	}, [
		color.text.primary,
		color.text.secondary,
		formGroup,
		instances,
		user?.isDeveloper,
		offlineFormGroup?.syncedAt,
		offlineFormGroup?.updatedAt,
		formGroup.lastUpdatedBy,
		props.forceUpdate,
	]);

	function toggleExpandMenu() {
		setExpanded(!expanded);
	}

	function handleSelectFormGroup() {
		handleSelect(formGroup);
		setExpanded(false);
	}
	const handleClickOnOfflineFormGroup = () => {
		if (props.setOpenToast && props.setToastContent) {
			props.setOpenToast(true);
			props.setToastContent({
				title: "Offline Form Group",
				message: "This form group is offline only and cannot be opened while online. Please sync it online or switch to offline mode to view it.",
				type: "info",
			});
		}
	};

	return (
		<div
			style={{
				background: expanded ? color.background.tertiary : color.background.primary,
			}}
			key={formGroup.id}
			data-test="form-group-list-item-wrapper"
			data-formgroupid={formGroup.id}
			data-formgroupname={formGroup.name}>
			<div
				id="formGroupItem"
				style={{
					background: expanded ? color.background.tertiary : color.background.primary,
					borderBottom: expanded ? "none" : `1px solid ${color.border.primary}`,
				}}
				className={!location.pathname.includes("sync-menu") ? styles.formGroup_item : styles.syncFormGroup_item}>
				<div className={styles.checkboxWrapper}>
					<GoDotFill color={syncedColorState ? syncedColorState : "transparent"} />
					<input
						name={formGroup.id}
						className={styles.item_box}
						type="checkbox"
						checked={selected.indexOf(formGroup.id) > -1}
						onChange={handleSelectFormGroup}
					/>
				</div>
				<div
					data-test={`form-group-item-${formGroupName}`}
					onClick={() => (formGroup.offline ? handleClickOnOfflineFormGroup() : handleOpen(formGroup))}
					className={styles.item_name_btn}
					style={{ cursor: formGroup.offline ? "not-allowed" : "pointer" }}>
					<div className={styles.summary}>
						{editingText ? (
							<TextFieldWrapper
								type="text"
								autoFocus
								colorPalette={color}
								value={formGroupName}
								name={`Edit ${formGroup.name} name`}
								title={`Edit ${formGroup.name} name`}
								data-test={`edit-form-group-name-${formGroupName}`}
								onBlur={async () => {
									setEditingText(false);
									await handleNameUpdate();
								}}
								onChange={(e) => {
									e.stopPropagation();
									setFormGroupName(e.target.value);
								}}
								onKeyUp={async (e) => {
									e.preventDefault();
									if (e.key === "Enter") {
										setEditingText(false);
										await handleNameUpdate();
									} else if (e.key === "Escape") {
										setFormGroupName(formGroup.name);
										setEditingText(false);
									}
								}}
								onClick={(e) => {
									e.stopPropagation();
								}}
							/>
						) : (
							<div
								style={{ color: color.text.primary }}
								className={styles.item_name_text}
								data-test={`form-group-name-${formGroupName}`}
								title={`${formGroupName} Folder`}>
								{formGroupName}
								{formGroup.offlineAndOnline && (
									<SavedLocally color={color.text.primary} style={{ marginLeft: "8px", verticalAlign: "middle" }} />
								)}
							</div>
						)}
						{!location.pathname.includes("sync-menu") && (
							<Button
								colorPalette={color}
								disabled={loading || formGroup.offline}
								onClick={(e) => {
									e.stopPropagation();
									if (!editingText) {
										setEditingText(true);
									} else {
										setEditingText(false);
									}
								}}
								title="Edit Formgroup Name"
								data-test={`edit-form-group-name-btn-${formGroupName}`}
								name={`Toggle Edit ${formGroup.name} Name`}
								variant="outlined"
								icon={<FiEdit />}
								style={{ border: "none", background: "transparent", cursor: formGroup.offline ? "not-allowed" : "pointer" }}
							/>
						)}
					</div>
				</div>
				{expanded ? (
					<Button
						colorPalette={color}
						onClick={toggleExpandMenu}
						icon={<FiChevronUp size={24} />}
						variant="outlined"
						style={{ border: "none", background: "transparent" }}
					/>
				) : (
					<Button
						colorPalette={color}
						onClick={toggleExpandMenu}
						icon={<FiChevronDown size={24} />}
						variant="outlined"
						style={{ border: "none", background: "transparent" }}
					/>
				)}
			</div>
			<div
				id="expandedFormGroup"
				style={{
					backgroundColor: color.background.tertiary,
					borderTop: expanded ? `1px solid ${color.border.primary}` : "none",
				}}
				className={expanded ? styles.menu_wrapper : styles.menuWrapper_closed}>
				{menu}
			</div>
		</div>
	);
}

export default FormGroupListItem;
